import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { clearPartnerCatalogToken } from '../utils/partnerCatalogStorage';

const SESSION_BRANDING_KEY = 'dvn.clientSessionBranding';
const SESSION_BRANDING_EVENT = 'dvn:session-branding';

const ClientBrandingContext = createContext({
  branding: null,
  setClientBranding: () => {},
});

export const isPartnerClientBrand = (branding) => (
  Boolean(branding && branding.level && branding.level !== 'house')
);

const readStoredSessionBranding = () => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(SESSION_BRANDING_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return isPartnerClientBrand(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

export const persistClientSessionBranding = (branding) => {
  if (typeof window === 'undefined') return;
  if (isPartnerClientBrand(branding)) {
    window.localStorage.setItem(SESSION_BRANDING_KEY, JSON.stringify(branding));
  } else {
    window.localStorage.removeItem(SESSION_BRANDING_KEY);
  }
  window.dispatchEvent(new Event(SESSION_BRANDING_EVENT));
};

export const clearClientSessionBranding = () => {
  clearPartnerCatalogToken();
  persistClientSessionBranding(null);
};

/**
 * Partner marks for traveller-facing chrome (navbar, trip header) and the
 * shared document theme. Page branding (a trip or package) wins; otherwise a
 * Bespoke client-invite session keeps the partner theme on My Trips and the rest
 * of the traveller app.
 */
export const ClientBrandingProvider = ({ children }) => {
  const [pageBranding, setClientBranding] = useState(null);
  const [sessionBranding, setSessionBranding] = useState(readStoredSessionBranding);

  useEffect(() => {
    const sync = () => setSessionBranding(readStoredSessionBranding());
    window.addEventListener(SESSION_BRANDING_EVENT, sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener(SESSION_BRANDING_EVENT, sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  const branding = pageBranding || sessionBranding;
  const value = useMemo(
    () => ({ branding, setClientBranding }),
    [branding]
  );
  return (
    <ClientBrandingContext.Provider value={value}>
      {children}
    </ClientBrandingContext.Provider>
  );
};

export const useClientBranding = () => useContext(ClientBrandingContext);

export const useSyncClientBranding = (branding) => {
  const { setClientBranding } = useClientBranding();
  useEffect(() => {
    if (isPartnerClientBrand(branding)) {
      setClientBranding(branding);
    } else {
      setClientBranding(null);
    }
    return () => setClientBranding(null);
  }, [branding, setClientBranding]);
};
